import {
  ComputedFieldProps,
  DropdownOption,
} from "@dbn/omnichannel-design-system-g4p";
import { option } from "fp-ts";
import { Option } from "fp-ts/Option";
import { pipe, constVoid, constant } from "fp-ts/function";
import { useFormatIncomeCurrency, useFormatMessage } from "../../../intl";
import { useQuery } from "../../../useAPI";
import * as api from "../api";
import * as remoteData from "../../../RemoteData";
import { DropdownField } from "../../../Common/DropdownField/DropdownField";
import { selectedDropdownOption } from "../../../Common/selectDropdownOption";
import { useEffect, useState } from "react";
import { ReadOnlyFieldToValue } from "../../../Common/ReadOnlyField/ReadOnlyFieldToValue";

type Props = {
  fieldProps: ComputedFieldProps<Option<string>>;
  isMainIncome: boolean;
};

export function CurrencyDropdown(props: Props) {
  const formatMessage = useFormatMessage();
  const formatCurrency = useFormatIncomeCurrency();

  const [currenciesResponse] = useQuery(api.currencies);
  const [currencies, setCurrencies] = useState<string[]>([]);

  const options: DropdownOption<string>[] = currencies.map(
    (currency: string) => ({
      value: currency,
      label: formatCurrency(currency, "long"),
    })
  );

  const onChange = (value: Option<DropdownOption<string>>) => {
    props.fieldProps.onChange(
      pipe(
        value,
        option.map(v => v.value)
      )
    );
  };

  useEffect(() => {
    pipe(
      currenciesResponse,
      remoteData.fold(constVoid, constVoid, curr => {
        setCurrencies(curr);
      })
    );
  }, [currenciesResponse]);

  const initialValue = pipe(props.fieldProps.value);

  const value = selectedDropdownOption(initialValue, options);

  return props.fieldProps.disabled ? (
    <ReadOnlyFieldToValue
      value={pipe(
        value,
        option.map(({ label }) => String(label)),
        option.getOrElse(constant(""))
      )}
      label={
        props.isMainIncome
          ? formatMessage("StandardLoan.EmployedCard.salaryCurrency")
          : formatMessage("StandardLoan.EmployedCard.additionalSalaryCurrency")
      }
    />
  ) : (
    <DropdownField
      {...props.fieldProps}
      clearable={false}
      label={
        props.isMainIncome
          ? formatMessage("StandardLoan.EmployedCard.salaryCurrency")
          : formatMessage("StandardLoan.EmployedCard.additionalSalaryCurrency")
      }
      onChange={onChange}
      options={options}
      placeholder={formatMessage(
        "StandardLoan.EmployedCard.salaryCurrencyPlaceholder"
      )}
      searchable
      value={value}
    />
  );
}
